import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, concatMap, switchMap, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { AppState } from '@get/store/configs/reducers';
import { StoreActionType } from '@enums';
import { getMultiAction } from '@get/store/configs/batched-actions';
import { SocieteCaracteristiqueChoix, SocieteCaracteristiqueChoixEntityState } from '@get/api-interfaces';
import { SocieteCaracteristiqueChoixApiService } from '@get/store/api-services';
import { SocieteCaracteristiqueChoixGeneratedActions } from '@get/store/actions';
import { getActionsToNormalizeSocieteCaracteristiqueChoix } from '@get/store/configs/normalization';
import { SocieteCaracteristiqueChoixSelectors } from '@get/store/selectors';
import { SocieteCaracteristiqueChoixRelationsIds } from '@get/store/ids-interfaces';
import { SocieteCaracteristiqueGeneratedActions } from '@get/store/actions';
import { FichierGeneratedActions } from '@get/store/actions';
import { CaracteristiqueChoixTemplateGeneratedActions } from '@get/store/actions';
import { ValeurGeneratedActions } from '@get/store/actions';
import { Valeur } from '@get/api-interfaces';

export function getDefaultAddSocieteCaracteristiqueChoixActions(societeCaracteristiqueChoix: SocieteCaracteristiqueChoixEntityState, ids?: SocieteCaracteristiqueChoixRelationsIds): Action[] {
  const actions: Action[] = [SocieteCaracteristiqueChoixGeneratedActions.normalizeManySocieteCaracteristiqueChoicesAfterUpsert({ societeCaracteristiqueChoices: [societeCaracteristiqueChoix] })];

  if (ids?.societeCaracteristique) {
    actions.push(
      SocieteCaracteristiqueGeneratedActions.addManySocieteCaracteristiqueChoixSuccess({
        idSocieteCaracteristique: ids.societeCaracteristique,
        idSocieteCaracteristiqueChoices: [societeCaracteristiqueChoix.idSocieteCaracteristiqueChoix]
      })
    );
    actions.push(
      SocieteCaracteristiqueChoixGeneratedActions.addSocieteCaracteristiqueSuccess({
        idSocieteCaracteristiqueChoix: societeCaracteristiqueChoix.idSocieteCaracteristiqueChoix,
        idSocieteCaracteristique: ids.societeCaracteristique
      })
    );
  }

  if (ids?.fichier) {
    actions.push(
      FichierGeneratedActions.addManySocieteCaracteristiqueChoixSuccess({
        idFichier: ids.fichier,
        idSocieteCaracteristiqueChoices: [societeCaracteristiqueChoix.idSocieteCaracteristiqueChoix]
      })
    );
    actions.push(
      SocieteCaracteristiqueChoixGeneratedActions.addFichierSuccess({
        idSocieteCaracteristiqueChoix: societeCaracteristiqueChoix.idSocieteCaracteristiqueChoix,
        idFichier: ids.fichier
      })
    );
  }

  if (ids?.caracteristiqueChoixTemplate) {
    actions.push(
      CaracteristiqueChoixTemplateGeneratedActions.addManySocieteCaracteristiqueChoixSuccess({
        idCaracteristiqueChoixTemplate: ids.caracteristiqueChoixTemplate,
        idSocieteCaracteristiqueChoices: [societeCaracteristiqueChoix.idSocieteCaracteristiqueChoix]
      })
    );
    actions.push(
      SocieteCaracteristiqueChoixGeneratedActions.addCaracteristiqueChoixTemplateSuccess({
        idSocieteCaracteristiqueChoix: societeCaracteristiqueChoix.idSocieteCaracteristiqueChoix,
        idCaracteristiqueChoixTemplate: ids.caracteristiqueChoixTemplate
      })
    );
  }

  if (ids?.valeurs) {
    if (!Array.isArray(ids.valeurs)) {
      actions.push(
        ValeurGeneratedActions.upsertOneValeur({
          valeur: {
            idSocieteCaracteristiqueChoix: societeCaracteristiqueChoix.idSocieteCaracteristiqueChoix,
            idValeur: ids.valeurs as number
          } as Valeur
        })
      );
      actions.push(
        SocieteCaracteristiqueChoixGeneratedActions.addManyValeurSuccess({
          idSocieteCaracteristiqueChoix: societeCaracteristiqueChoix.idSocieteCaracteristiqueChoix,
          idValeurs: [ids.valeurs as number]
        })
      );
    } else {
      actions.push(
        ValeurGeneratedActions.upsertManyValeurs({
          valeurs: (ids.valeurs as number[]).map(
            (idValeur: number) => ({
              idSocieteCaracteristiqueChoix: societeCaracteristiqueChoix.idSocieteCaracteristiqueChoix,
              idValeur
            })
          ) as Valeur[]
        })
      );
      actions.push(
        SocieteCaracteristiqueChoixGeneratedActions.addManyValeurSuccess({
          idSocieteCaracteristiqueChoix: societeCaracteristiqueChoix.idSocieteCaracteristiqueChoix,
          idValeurs: ids.valeurs as number[]
        })
      );
    }
  }

  return actions;
}

export function getDefaultDeleteSocieteCaracteristiqueChoixActions(societeCaracteristiqueChoix: SocieteCaracteristiqueChoixEntityState): Action[] {
  const actions: Action[] = [SocieteCaracteristiqueChoixGeneratedActions.deleteOneSocieteCaracteristiqueChoixSuccess({ idSocieteCaracteristiqueChoix: societeCaracteristiqueChoix.idSocieteCaracteristiqueChoix })];

  if (societeCaracteristiqueChoix.societeCaracteristique) {
    actions.push(
      SocieteCaracteristiqueGeneratedActions.deleteManySocieteCaracteristiqueChoixSuccess({
        idSocieteCaracteristiqueChoices: [societeCaracteristiqueChoix.idSocieteCaracteristiqueChoix],
        idSocieteCaracteristiques: [societeCaracteristiqueChoix.societeCaracteristique as number]
      })
    );
  }

  if (societeCaracteristiqueChoix.fichier) {
    actions.push(
      FichierGeneratedActions.deleteManySocieteCaracteristiqueChoixSuccess({
        idSocieteCaracteristiqueChoices: [societeCaracteristiqueChoix.idSocieteCaracteristiqueChoix],
        idFichiers: [societeCaracteristiqueChoix.fichier as number]
      })
    );
  }

  if (societeCaracteristiqueChoix.caracteristiqueChoixTemplate) {
    actions.push(
      CaracteristiqueChoixTemplateGeneratedActions.deleteManySocieteCaracteristiqueChoixSuccess({
        idSocieteCaracteristiqueChoices: [societeCaracteristiqueChoix.idSocieteCaracteristiqueChoix],
        idCaracteristiqueChoixTemplates: [societeCaracteristiqueChoix.caracteristiqueChoixTemplate as number]
      })
    );
  }

  if (societeCaracteristiqueChoix.valeurs) {
    actions.push(
      ValeurGeneratedActions.deleteManySocieteCaracteristiqueChoixSuccess({
        idSocieteCaracteristiqueChoices: [societeCaracteristiqueChoix.idSocieteCaracteristiqueChoix],
        idValeurs: societeCaracteristiqueChoix.valeurs as number[]
      })
    );
  }

  return actions;
}

export class GeneratedSocieteCaracteristiqueChoixEffects {
  constructor(
    protected actions$: Actions,
    protected societeCaracteristiqueChoixApiService: SocieteCaracteristiqueChoixApiService,
    protected store$: Store<AppState>
  ) {}

  getManySocieteCaracteristiqueChoices$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SocieteCaracteristiqueChoixGeneratedActions.getManySocieteCaracteristiqueChoices),
      switchMap(({ params }) =>
        this.societeCaracteristiqueChoixApiService.getSocieteCaracteristiqueChoices(params).pipe(
          map((societeCaracteristiqueChoices: SocieteCaracteristiqueChoix[]) => {
            return SocieteCaracteristiqueChoixGeneratedActions.normalizeManySocieteCaracteristiqueChoicesAfterUpsert({ societeCaracteristiqueChoices });
          }),
          catchError(error => of(SocieteCaracteristiqueChoixGeneratedActions.societeCaracteristiqueChoicesFailure({ error })))
        )
      )
    );
  });

  getOneSocieteCaracteristiqueChoix$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SocieteCaracteristiqueChoixGeneratedActions.getOneSocieteCaracteristiqueChoix),
      switchMap(idSocieteCaracteristiqueChoix =>
        this.societeCaracteristiqueChoixApiService.getSocieteCaracteristiqueChoix(idSocieteCaracteristiqueChoix).pipe(
          map((societeCaracteristiqueChoix: SocieteCaracteristiqueChoix) => {
            return SocieteCaracteristiqueChoixGeneratedActions.normalizeManySocieteCaracteristiqueChoicesAfterUpsert({ societeCaracteristiqueChoices: [societeCaracteristiqueChoix] });
          }),
          catchError(error => of(SocieteCaracteristiqueChoixGeneratedActions.societeCaracteristiqueChoicesFailure({ error })))
        )
      )
    );
  });

  upsertOneSocieteCaracteristiqueChoix$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SocieteCaracteristiqueChoixGeneratedActions.upsertOneSocieteCaracteristiqueChoix),
      concatMap(
        ({
          societeCaracteristiqueChoix,
          ids
        }: {
          societeCaracteristiqueChoix: Partial<SocieteCaracteristiqueChoix>;
          ids?: SocieteCaracteristiqueChoixRelationsIds;
        }) => {
          if (societeCaracteristiqueChoix.idSocieteCaracteristiqueChoix) {
            return this.societeCaracteristiqueChoixApiService.updateSocieteCaracteristiqueChoix(societeCaracteristiqueChoix).pipe(
              map((societeCaracteristiqueChoixReturned: SocieteCaracteristiqueChoix) => {
                return SocieteCaracteristiqueChoixGeneratedActions.normalizeManySocieteCaracteristiqueChoicesAfterUpsert({ societeCaracteristiqueChoices: [societeCaracteristiqueChoixReturned] });
              }),
              catchError(error => of(SocieteCaracteristiqueChoixGeneratedActions.societeCaracteristiqueChoicesFailure({ error })))
            );
          } else {
            return this.societeCaracteristiqueChoixApiService.addSocieteCaracteristiqueChoix(societeCaracteristiqueChoix).pipe(
              mergeMap((societeCaracteristiqueChoixReturned: SocieteCaracteristiqueChoix) => getDefaultAddSocieteCaracteristiqueChoixActions(societeCaracteristiqueChoixReturned, ids)),
              catchError(error => of(SocieteCaracteristiqueChoixGeneratedActions.societeCaracteristiqueChoicesFailure({ error })))
            );
          }
        }
      )
    );
  });

  deleteOneSocieteCaracteristiqueChoix$ = createEffect(() => {
    const selectSocieteCaracteristiqueChoixState$ = this.store$.select(SocieteCaracteristiqueChoixSelectors.selectSocieteCaracteristiqueChoixState);
    return this.actions$.pipe(
      ofType(SocieteCaracteristiqueChoixGeneratedActions.deleteOneSocieteCaracteristiqueChoix),
      withLatestFrom(selectSocieteCaracteristiqueChoixState$),
      concatMap(([{ idSocieteCaracteristiqueChoix }, state]) =>
        this.societeCaracteristiqueChoixApiService.deleteSocieteCaracteristiqueChoix(idSocieteCaracteristiqueChoix).pipe(
          mergeMap(_success => getDefaultDeleteSocieteCaracteristiqueChoixActions(state.entities[idSocieteCaracteristiqueChoix] as SocieteCaracteristiqueChoixEntityState)),
          catchError(error => of(SocieteCaracteristiqueChoixGeneratedActions.societeCaracteristiqueChoicesFailure({ error })))
        )
      )
    );
  });

  normalizeManySocieteCaracteristiqueChoicesAfterUpsert$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SocieteCaracteristiqueChoixGeneratedActions.normalizeManySocieteCaracteristiqueChoicesAfterUpsert),
      concatMap(({ societeCaracteristiqueChoices }) => {
        const actions: Action[] = getActionsToNormalizeSocieteCaracteristiqueChoix(societeCaracteristiqueChoices, StoreActionType.upsert);
        return [getMultiAction(actions, '[SocieteCaracteristiqueChoix] Normalization After Upsert Success')];
      })
    );
  });
}
